import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import backend from '../backend';

export type TokenIntegraItem = {
  id: number;
  nome?: string;
  name?: string;
  abilities: string[];
  last_used_at?: string | null;
  expires_at?: string | null;
  created_at?: string | null;
  expirado?: boolean;
};

export type OpcaoPermissao = {
  chave: string;
  nome: string;
  descricao: string;
  colorScheme: string;
};

type UseTokensIntegraParams = {
  empresaId?: number | string | null;
  filialId?: number | string | null;
};

type GerarTokenPayload = {
  nome: string;
  duracao_meses: number;
  abilities: string[];
};

type RevogarTokenPayload = {
  tokenId: number;
};

type PermissaoApiItem = {
  chave: string;
  nome: string;
  descricao: string;
};

const obterColorSchemePermissao = (chave: string) => {
  if (chave.includes('delete')) return 'red';
  if (chave.includes('update')) return 'orange';
  if (chave.includes('create')) return 'green';

  return 'blue';
};

export function useTokensIntegra({
  empresaId,
  filialId,
}: UseTokensIntegraParams) {
  const queryClient = useQueryClient();
  const [revogandoTokenId, setRevogandoTokenId] = useState<number | null>(null);

  const tokensQueryKey = ['integra-tokens', empresaId, filialId];

  const tokensQuery = useQuery<TokenIntegraItem[]>(
    tokensQueryKey,
    async () => {
      const response = await backend.get('/integra/tokens', {
        params: {
          empresa_id: empresaId,
          filial_id: filialId,
        },
      });

      return response.data?.data || [];
    },
    {
      enabled: Boolean(empresaId && filialId),
    },
  );

  const abilitiesQuery = useQuery<OpcaoPermissao[]>(
    ['integra-token-abilities'],
    async () => {
      const response = await backend.get('/integra/token/abilities');

      return (response.data?.data || []).map((item: PermissaoApiItem) => ({
        chave: item.chave,
        nome: item.nome,
        descricao: item.descricao,
        colorScheme: obterColorSchemePermissao(item.chave),
      }));
    },
  );

  const gerarTokenMutation = useMutation(
    async (payload: GerarTokenPayload) => {
      const response = await backend.post('/integra/tokens', {
        nome: payload.nome,
        empresa_id: empresaId,
        filial_id: filialId,
        duracao_meses: payload.duracao_meses,
        abilities: payload.abilities,
      });

      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(tokensQueryKey);
      },
    },
  );

  const revogarTokenMutation = useMutation(
    async ({ tokenId }: RevogarTokenPayload) => {
      setRevogandoTokenId(tokenId);

      const response = await backend.delete(`/integra/tokens/${tokenId}`, {
        params: {
          empresa_id: empresaId,
          filial_id: filialId,
        },
      });

      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(tokensQueryKey);
      },
      onSettled: () => {
        setRevogandoTokenId(null);
      },
    },
  );

  return {
    tokens: tokensQuery.data || [],
    opcoesPermissoes: abilitiesQuery.data || [],

    carregandoTokens: tokensQuery.isLoading,
    carregandoPermissoes: abilitiesQuery.isLoading,
    gerandoToken: gerarTokenMutation.isLoading,
    revogandoToken: revogarTokenMutation.isLoading,
    revogandoTokenId,

    erroTokens: tokensQuery.isError,
    erroPermissoes: abilitiesQuery.isError,

    recarregarTokens: tokensQuery.refetch,
    recarregarPermissoes: abilitiesQuery.refetch,

    gerarToken: gerarTokenMutation.mutateAsync,
    revogarToken: revogarTokenMutation.mutateAsync,
  };
}
